// frontend/src/hooks/chat/index.ts
/**
 * Chat Hooks Package
 *
 * Modular organization of chat functionality:
 * - utils.ts: Shared utilities (storage, auth, message helpers)
 * - useAgentV4Chat.ts: Agent V4 hybrid deterministic-AI chat (primary)
 *
 * This index provides the main useChat hook that composes all functionality.
 *
 * V4 Migration: Agent V4 is now the only chat system.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { typedApi } from '../../services/typedApiClient';
import {
  loadMessagesFromStorage,
  saveMessagesToStorage,
  clearMessagesFromStorage,
  FALLBACK_MODELS,
  type ChatMessage,
  type ChatAction,
  type ChatModel,
  type SessionStats
} from './utils';
import { useAgentV4Chat, type UseAgentV4ChatReturn, type IntentData } from './useAgentV4Chat';
import type { SuggestionsPayload } from '../academicPlan/usePlanCore';
import { getErrorMessage } from '../../utils/errorUtils';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Transformed action for UI display
 */
export interface TransformedAction {
  id: string;
  tool: string;
  description: string;
  params: Record<string, unknown>;
}

/**
 * V4 features info
 */
export interface V4Features {
  intent_classification: boolean;
  deterministic_validation: boolean;
  research_agent: boolean;
  streaming: boolean;
}

/**
 * V4 state exposed to components
 */
export interface V4State {
  // Status (V4 is always enabled)
  isAvailable: boolean;
  isEnabled: boolean;
  version: string;
  features: V4Features;
  statusLoading: boolean;

  // Chat functions
  sendMessage: (content: string, action?: string) => Promise<void>;
  confirmActions: () => Promise<void>;
  rejectActions: () => Promise<void>;
  resetSession: () => Promise<void>;
  cancelStreaming: () => void;

  // State
  pendingActions: ChatAction[];
  isConfirming: boolean;
  isStreaming: boolean;
  hasPendingActions: boolean;
  lastIntent: IntentData | null;
  lastMetrics: unknown;
}

/**
 * Return type for the useChat hook
 */
export interface UseChatReturn {
  // State
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  selectedModel: string;
  setSelectedModel: (modelId: string) => void;
  availableModels: ChatModel[];
  modelsLoading: boolean;
  getCurrentModel: () => ChatModel | null;

  // Primary send method (V4 streaming)
  sendMessage: (content: string, action?: string) => Promise<void>;

  // Chat management
  clearChat: () => void;
  sessionStats: SessionStats;

  // Session management
  storeSession: () => Promise<void>;
  sessionSummarized: boolean;

  // Action confirmation (V4)
  pendingActions: TransformedAction[];
  executedActions: TransformedAction[];
  blockedActions: TransformedAction[];
  confirmActions: (approvedActions: TransformedAction[], rejectedActionIds: string[]) => Promise<void>;
  rejectAllActions: () => Promise<void>;
  isConfirming: boolean;
  hasPendingActions: boolean;

  // Streaming controls
  streamingEnabled: boolean;
  setStreamingEnabled: (enabled: boolean) => void;
  isStreaming: boolean;
  cancelStreaming: () => void;

  // V4 specific state (for components that want direct access)
  v4: V4State;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format a V4 action into a human-readable description.
 */
function formatActionDescription(action: ChatAction): string {
  const { action_type, parameters = {} } = action;

  switch (action_type) {
    case 'add_course':
      return `Add ${parameters.course_code || 'course'} to ${parameters.semester || 'semester'}`;
    case 'remove_course':
      return `Remove ${parameters.course_code || 'course'} from ${parameters.semester || 'semester'}`;
    case 'move_course':
      return `Move ${parameters.course_code || 'course'} to ${parameters.target_semester || 'semester'}`;
    case 'add_semester':
      return `Add semester ${parameters.semester || ''}`;
    case 'remove_semester':
      return `Remove semester ${parameters.semester || ''}`;
    case 'add_major':
      return `Add major: ${parameters.program_name || parameters.major_code || 'major'}`;
    case 'remove_major':
      return `Remove major: ${parameters.program_name || parameters.major_code || 'major'}`;
    case 'add_minor':
      return `Add minor: ${parameters.program_name || parameters.minor_code || 'minor'}`;
    case 'remove_minor':
      return `Remove minor: ${parameters.program_name || parameters.minor_code || 'minor'}`;
    default:
      // Format the action_type as readable text
      return action_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Main chat hook that composes all chat functionality.
 *
 * @param userId User ID
 * @param academicPlanId Academic plan ID
 * @param supabaseUserId Supabase user ID
 * @param onPlanUpdate Callback when plan is updated
 */
export const useChat = (
  userId: string | null,
  academicPlanId: number | string | null,
  supabaseUserId: string | null,
  onPlanUpdate?: () => Promise<void>,
  isAdmin?: boolean,
  onClearSuggestionsLocally?: () => void,
  onMergeSuggestionsLocally?: (payload: SuggestionsPayload) => void,
): UseChatReturn => {
  // Hydrated synchronously so the first paint already has the thread. The id
  // this initial read covers is recorded in hydratedPlanIdRef below, which is
  // what keeps the persist/hydrate pass (further down) from writing over a
  // plan id it has not read yet.
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadMessagesFromStorage(academicPlanId));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState('sonnet-3.5');
  const [availableModels, setAvailableModels] = useState<ChatModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Session stats
  const [sessionStats, setSessionStats] = useState<SessionStats>({
    totalTokens: 0,
    totalCost: 0,
    messageCount: 0,
    startTime: new Date().toISOString()
  });

  // Streaming state
  const [streamingEnabled, setStreamingEnabled] = useState(true);

  // Session management
  const [sessionSummarized, setSessionSummarized] = useState(false);

  // Refs
  const isMounted = useRef(true);
  // The plan id whose stored thread `messages` currently reflects. Seeded from
  // the lazy initializer above: a falsy id reads nothing, so it stays unhydrated.
  const hydratedPlanIdRef = useRef<number | string | null>(academicPlanId || null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Agent V4 chat hook (the only chat system)
  const v4: UseAgentV4ChatReturn = useAgentV4Chat({
    messages,
    setMessages,
    setIsLoading,
    setError,
    setSessionStats,
    academicPlanId,
    isMounted,
    onPlanUpdate,
    isAdmin,
    onClearSuggestionsLocally,
    onMergeSuggestionsLocally,
  });

  // Load available models from backend
  const loadAvailableModels = useCallback(async (): Promise<void> => {
    try {
      setModelsLoading(true);
      const data = await typedApi.chat.getModels();
      const modelsData = ((data as { models?: ChatModel[] }).models || []) as ChatModel[];
      setAvailableModels(modelsData);

      if (!selectedModel && modelsData.length > 0) {
        const defaultModel = modelsData.find(m => m.default) || modelsData[0];
        setSelectedModel(defaultModel.id);
      }
    } catch (err) {
      console.error('Failed to load available models:', err);
      setAvailableModels(FALLBACK_MODELS);
    } finally {
      setModelsLoading(false);
    }
  }, [selectedModel]);

  // Load models on mount
  useEffect(() => {
    loadAvailableModels();
  }, [loadAvailableModels]);

  // NOTE: the per-chat-open fetch of /api/academic-plans/<id>/ai-context was
  // removed (Phase 4b, docs/plans/chatbot-credit-grounding.md): its result was
  // stored but consumed by nothing and never sent to the V4 chat — the backend
  // assembles chat context server-side. Removing it saves a request per chat open.

  // Summarize pending session on chat initialization
  const summarizePendingSession = useCallback(async (): Promise<void> => {
    if (!supabaseUserId || sessionSummarized) return;

    try {
      await typedApi.studentProfile.summarizeSession();
      setSessionSummarized(true);
    } catch (err) {
      console.warn('Failed to summarize pending session:', getErrorMessage(err));
      setSessionSummarized(true);
    }
  }, [supabaseUserId, sessionSummarized]);

  // Store session for later summarization
  // Uses messagesRef to always get latest messages without causing callback identity changes
  const storeSessionForSummarization = useCallback(async (): Promise<void> => {
    const currentMessages = messagesRef.current;
    if (!supabaseUserId || currentMessages.length < 2) return;

    try {
      const sessionMessages = currentMessages.map(({ role, content }) => ({ role, content }));
      await typedApi.studentProfile.storeSession({ messages: sessionMessages } as never);
    } catch (err) {
      console.warn('Failed to store session:', getErrorMessage(err));
    }
  }, [supabaseUserId]);

  // Summarize pending session when chat initializes
  useEffect(() => {
    if (supabaseUserId && !sessionSummarized) {
      summarizePendingSession();
    }
  }, [supabaseUserId, sessionSummarized, summarizePendingSession]);

  // Hydrate a plan id exactly once, before anything is written under it.
  //
  // This was two effects — persist, then reload-on-plan-change — and because
  // effects run in declaration order the persist pass always won the race on a
  // plan-id change, writing the OUTGOING plan's messages under the INCOMING
  // plan's key before the reload pass could read it. That produced two bugs
  // from one defect: an empty outgoing thread deleted the incoming one (the
  // navigation "reset"), and a non-empty one overwrote it (plan A's
  // conversation displayed under plan B, and across a school switch, sent to
  // the other institution's backend as conversation history).
  //
  // One effect with an explicit "have I read this id yet" ref removes the race
  // rather than reordering it: the first pass for an id reads, every later pass
  // writes. Hydrating sets state, which re-runs this effect with the ref now
  // matching, so the write still happens on the next pass.
  //
  // Known narrow gap: a message mutation landing in the SAME commit as a
  // plan-id change is not persisted under the outgoing id. Plan switches go
  // through an async fetch, so that needs a streaming chunk to arrive in
  // exactly that commit — and such a stream is orphaned by the switch anyway.
  useEffect(() => {
    if (!academicPlanId) return;

    if (hydratedPlanIdRef.current !== academicPlanId) {
      hydratedPlanIdRef.current = academicPlanId;
      setMessages(loadMessagesFromStorage(academicPlanId));
      return;
    }

    saveMessagesToStorage(academicPlanId, messages);
  }, [academicPlanId, messages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Clear chat
  const clearChat = useCallback((): void => {
    setMessages([]);
    clearMessagesFromStorage(academicPlanId);
    setSessionStats({
      totalTokens: 0,
      totalCost: 0,
      messageCount: 0,
      startTime: new Date().toISOString()
    });
    v4.clearV4State();
  }, [academicPlanId, v4]);

  // Model selection helper
  const selectModel = useCallback((modelId: string): void => {
    const model = availableModels.find(m => m.id === modelId);
    if (model) {
      setSelectedModel(modelId);
    }
  }, [availableModels]);

  // Get current model info
  const getCurrentModel = useCallback((): ChatModel | null => {
    return availableModels.find(m => m.id === selectedModel) || null;
  }, [availableModels, selectedModel]);

  // Transform V4 pending actions to match the UI format expected by PendingActions component
  // V4 actions: { action_type: 'add_course', parameters: { course_code: 'CMSC216' } }
  // UI expects: { id: '...', tool: 'add_course', description: '...', params: { ... } }
  const transformedPendingActions: TransformedAction[] = useMemo(() =>
    v4.pendingActions.map((action, index) => ({
      id: `v4-action-${index}-${Date.now()}`,
      tool: action.action_type,
      description: formatActionDescription(action),
      params: action.parameters || {}
    })),
    [v4.pendingActions]
  );

  // Adapter for confirmActions to match old API signature
  // Old API: confirmActions(approvedActions, rejectedActionIds)
  // V4 API: confirmActions() confirms all, rejectActions() rejects all
  const confirmActionsAdapter = useCallback(async (
    approvedActions: TransformedAction[],
    _rejectedActionIds: string[]
  ): Promise<void> => {
    // If any actions are approved, confirm all (V4 is all-or-nothing)
    if (approvedActions && approvedActions.length > 0) {
      await v4.confirmActions();
    } else {
      // All rejected
      await v4.rejectActions();
    }
  }, [v4]);

  // Adapter for rejectAllActions
  const rejectAllActionsAdapter = useCallback(async (): Promise<void> => {
    await v4.rejectActions();
  }, [v4]);

  // V4 state object for direct access
  const v4State: V4State = useMemo(() => ({
    // Status (V4 is always enabled)
    isAvailable: true,
    isEnabled: true,
    version: '4.0.0',
    features: {
      intent_classification: true,
      deterministic_validation: true,
      research_agent: true,
      streaming: true
    },
    statusLoading: false,

    // Chat functions
    sendMessage: v4.sendV4StreamingMessage,
    confirmActions: v4.confirmActions,
    rejectActions: v4.rejectActions,
    resetSession: v4.resetSession,
    cancelStreaming: v4.cancelStreaming,

    // State
    pendingActions: v4.pendingActions,
    isConfirming: v4.isConfirming,
    isStreaming: v4.isStreaming,
    hasPendingActions: v4.hasPendingActions,
    lastIntent: v4.lastIntent,
    lastMetrics: v4.lastMetrics
  }), [v4]);

  return {
    // State
    messages,
    isLoading,
    error,
    selectedModel,
    setSelectedModel: selectModel,
    availableModels,
    modelsLoading,
    getCurrentModel,

    // Primary send method (V4 streaming)
    sendMessage: v4.sendV4StreamingMessage,

    // Chat management
    clearChat,
    sessionStats,

    // Session management
    storeSession: storeSessionForSummarization,
    sessionSummarized,

    // Action confirmation (V4)
    pendingActions: transformedPendingActions,
    executedActions: [], // V4 doesn't track these separately
    blockedActions: [], // V4 doesn't track these separately
    confirmActions: confirmActionsAdapter,
    rejectAllActions: rejectAllActionsAdapter,
    isConfirming: v4.isConfirming,
    hasPendingActions: v4.hasPendingActions,

    // Streaming controls
    streamingEnabled,
    setStreamingEnabled,
    isStreaming: v4.isStreaming,
    cancelStreaming: v4.cancelStreaming,

    // V4 specific state (for components that want direct access)
    // Note: V4 is now the only chat system, always available
    v4: v4State
  };
};

// Re-export sub-hooks and types for direct use if needed
export { useAgentV4Chat } from './useAgentV4Chat';
export type {
  ChatMessage,
  ChatAction,
  ChatModel,
  SessionStats,
  ChatMetrics,
  ApiMessage
} from './utils';
export type { UseAgentV4ChatParams, UseAgentV4ChatReturn, IntentData } from './useAgentV4Chat';
